<template>
  <div v-if="product" data-cy-region="product-page" class="container mx-auto">
    <div class="mt-4 px-5 md:px-0 flex items-center">
      <ClientOnly>
        <ProductPageBreadcrumb data-cy="product-page-breadcrumbs" class="[ md:hidden ] font-body" :threshold="2" />
        <ProductPageBreadcrumb data-cy="product-page-breadcrumbs" class="[ hidden md:flex ] font-body" />
      </ClientOnly>
    </div>

    <div class="flex flex-col md:flex-row mt-5">
      <ProductPageGallery />

      <div class="px-5 md:pl-6 md:pr-0">
        <div class="flex justify-between w-full items-start">
          <h1 class="product-name text-primary-1-100 text-xl-1 font-semibold">
            {{ product.name }}
          </h1>

          <div class="flex gap-x-4 items-center ml-8">
            <ShareButton :title="product.name || ''" class="text-secondary-1-100" />
            <span class="block w-1 h-1 bg-primary-1-100 rounded-full" />
            <FavoriteButton
              :sku="product.sku || ''"
              class="text-primary-1-100"
              @unfavorite="$emit('wishlist-removed')"
            />
          </div>
        </div>

        <div class="flex flex-col justify-between md:mt-4">
          <ProductPriceSection
            v-if="product"
            :product="product"
            :is-large="true"
            :is-bold="true"
            class="text-primary-1-100 mt-3 flex items-center lg:self-start gap-x-3"
          />

          <div class="flex items-center gap-x-4 mt-3">
            <span
              v-if="discount"
              data-attr="productCard_discount"
              class="text-sm lg:text-base uppercase bg-deals-35 text-white p-1.5 lg:px-2.5 font-medium"
            >
              {{ $n(discount, 'percent') }} {{ $t('off') }}
            </span>

            <span
              v-if="product.isNew"
              data-attr="productCard_specialOffer"
              class="text-sm bg-primary-1-100 text-white px-3 py-2"
            >
              {{ $t('isNew') }}
            </span>

            <span v-if="isLimitedStock" class="text-deals-35 uppercase">{{
              $t('limitedQty', { count: product.stock })
            }}</span>

            <span v-if="!product.stock" class="text-gray-90">
              {{ $t('outOfStock') }}
            </span>
          </div>
        </div>

        <section class="my-6">
          <div
            v-if="shippingLeadTime && shippingLeadTimeStatus === 'success'"
            class="flex items-center gap-x-4 text-sm text-primary-1-100"
          >
            <svg-icon-delivery-info class="w-6 h-6" />
            <p>{{ product.deliveryCondition || shippingLeadTime }}</p>
          </div>
          <div
            v-if="enableStorePickup"
            class="flex items-center gap-x-4 text-sm text-primary-1-100 border-t border-primary-1-10 pt-2 mt-2"
          >
            <svg-icon-store-pickup class="w-6 h-6" />
            <p>{{ $t('freeStorePickup') }}</p>
          </div>
        </section>

        <ProductVariantSelector
          v-if="product.variants"
          :product-variants="product.variants"
          data-cy="product-variants-selector"
        />

        <ProductGiftWrapping
          v-if="product && (product.simpleGiftBoxOptionId || product.configurableGiftBoxOptionId)"
          data-cy="gift-wrapping"
          class="mt-8 mb-7"
        />

        <ProductCartControls class="mt-5 hidden md:grid" />

        <ProductGroupCustomizer
          v-if="isGroupedProduct"
          class="order-last px-5 md:px-0 mt-5 border-t border-primary-200 pt-5"
        />

        <ProductPageDetails />
      </div>
    </div>

    <ProductStickyBar class="fixed md:hidden bottom-[84px] inset-x-0 z-10 py-4 px-6 bg-primary-2-30" />

    <ProductPageSuggestions class="mt-16" />

    <ProductPageRelatedProducts class="mt-16" />

    <ProductAttributesSlideShow v-model="sizeGuideToggler" />
  </div>
</template>

<script setup lang="ts">
import type { WithContext, Product as ProductJsonLdSchema } from 'schema-dts';
import { useStoreConfigShippingDetails } from '~/composables/storeConfig-shippingDetails';

const currencyCode = 'EGP'; // inject(CONFIG_STORE_CURRENCY);
const { isLoggedIn } = useAuth();
const { limited_qty_threshold, enableStorePickup } = useStoreConfig();
const { shippingLeadTime, status: shippingLeadTimeStatus } = useStoreConfigShippingDetails();
const { currentRoute } = useRouter();
const { redirect } = useAppRouter();

const { t: $t } = useI18n({
  useScope: 'local',
});

const route = useRoute();
const {
  public: { appUrl },
} = useRuntimeConfig();

const { product } = useProductContext({
  init: true,
});
const { onVisitProduct } = useVisitedProduct();
const { productView } = useGtmEvents();

/**
 * Watches the current route for changes and redirects to the last category page if the route matches the product's main URL key.
 *
 * @param {Object} currentRoute - The reactive property representing the current route.
 * @param {Object} newValue - The new value of the current route.
 */
watch(currentRoute, newValue => {
  if (process.server) return;

  const slug = newValue.params?.slug?.length ? newValue.params.slug : [];

  if (slug.length === 1 && slug[0] === product.value.url_key) {
    redirect(`${window.history.state.back}`);
  }
});

onMounted(() => {
  productView(product.value as any);

  if (isLoggedIn.value && product.value.sku) {
    onVisitProduct(product.value?.id?.toString() || '');
  }
});
defineEmits(['wishlist-removed']);

useAppSeoMeta(
  computed(() => ({
    title: product.value?.meta_title || product.value?.name,
    description: product.value?.meta_description,
    keywords: product.value?.meta_keywords,
    image: product.value?.media_gallery?.[0]?.url || product.value?.image?.url || '',
  })),
);

const isLimitedStock = computed(() => {
  return product.value?.stock && (limited_qty_threshold.value || Number.MIN_SAFE_INTEGER) >= product.value?.stock;
});

const discount = computed(() => {
  if (!product.value?.priceBefore) {
    return 0;
  }

  return (product.value?.priceBefore - product.value.price) / product.value.priceBefore;
});

const isGroupedProduct = computed(() => {
  return !!product.value?.groupItems?.length;
});

const sizeGuideToggler = ref(false);

onMounted(() => {
  // product.value && emit(TRACKING_EVENTS.PRODUCT_VIEW, product.value);
});

// async function addToCart() {
//   try {
//     await addItem();
//     emit('ITEM_ADDED', product.value);
//   } catch (err) {
//     if (/the requested qty is not available/i.test((err as CombinedError).message)) {
//       error(t('qtyUnavailable', [product.value?.name]) as string);
//       return;
//     }

//     if (
//       /Product that you are trying to add is not available/i.test((err as CombinedError).message) ||
//       /Some of the products are out of stock/i.test((err as CombinedError).message)
//     ) {
//       error(t('outOfStockError', [product.value?.name]) as string);
//     }

//     if (/requiredConfigurationOption/i.test((err as CombinedError).message)) {
//       error(t('error').toString(), t('requiredConfigurationOption').toString());
//     }
//     // eslint-disable-next-line no-console
//     console.log(err);
//   }
// }

/**
 * This is google's structured data for rich results feature
 * It let's google show the product in a rich preview panel if they want
 * https://developers.google.com/search/docs/data-types/product
 */
useJsonld(
  computed(() => {
    const url = appUrl;
    const data: WithContext<ProductJsonLdSchema> = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.value?.name as string,
      sku: product.value?.sku as string,
      image: toNonNullable(product.value?.media_gallery).map(img => img.url as string),
      description: product.value?.meta_description as string,
      brand: product.value?.brand
        ? {
            '@type': 'Brand',
            name: product.value.brand.name ?? '',
          }
        : undefined,
    };

    data.offers = {
      '@type': 'Offer',
      url: `${url}${route.fullPath}`,
      priceCurrency: currencyCode || 'EGP',
      price: product.value?.price,
      itemCondition: 'https://schema.org/NewCondition',
      availability: product.value?.stock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    };

    if (product.value?.review_count) {
      data.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: Number((product.value.rating_summary * 5) / 100).toFixed(1),
        reviewCount: product.value.review_count,
      };
    }

    return data;
  }).value,
);
</script>

<style lang="postcss" scoped>
.product-name {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>

<i18n>
{
  "en": {
    "off": "OFF",
    "specialOffer": "Special Offer",
    "reviewsCount": "1 Review | {count} Reviews",
    "startsFrom": "Starts from",
    "shippingEstimation": "Normally delivers in 3-5 business days at your doorstep",
    "limitedQty": "only {count} left",
    "addToCart": "Add To Cart",
    "addToWishlist": "Add to my wishlist",
    "qtyUnavailable": "The requested quantity of {0} is unavailable, please choose a lower quantity",
    "outOfStockError": "{0} is out of stock",
    "aboutTheProduct": "About the product",
    "isNew": "New-in ",
    "outOfStock": "Out Of Stock",
    "sizeGuide": "Size Guide"
  },
  "ar": {
    "off": "خصم",
    "specialOffer": "عرض خاص",
    "reviewsCount": "١ مراجعه| {count} مراجعات",
    "startsFrom": "يبدأ من",
    "shippingEstimation": "يتم الشحن خلال ٣-٥ ايام عمل",
    "limitedQty": "كمية محدودة",
    "addToCart": "اضف للعربة",
    "addToWishlist": "اضف للمفضلة",
    "qtyUnavailable": "الكمية المطلوبة من {0} غير متوفرة، من فضلك قم باختيار كمية أقل",
    "outOfStockError": "{0} غير متوفر",
    "aboutTheProduct": "عن المنتج",
    "isNew": "جديد",
    "outOfStock": "غير متاح",
    "sizeGuide": "دليل المقاسات"
  }
}
</i18n>
